import React, { useEffect, useMemo, useState } from "react";
import { Menu, Box, UnstyledButton, Group, Text, Tooltip } from "@mantine/core";
import CheckboxFilter from "../../filters/types/CheckboxFilter";
import { useIdeaFilters } from "../../../../hooks/useIdeaFilters";
import { useFiltersContextHelper } from "../../filters/useFiltersContextHelper";
import { IconChevronDown } from "@tabler/icons";
import { ProductAreaHeaderIcon } from "../../../../icons/x-symbol-svgrepo-com";
import {useIdeasUrlState} from "../../../../hooks/useIdeasUrlState";


const StickyProductAreaFilter: React.FC = ({ subPageKey }: any) => {
  const { ideasFiltersConfig } = useIdeaFilters();
  const {
    setSelectedParentFilter,
    setCheckedComponents,
    selectedParentFilter,
    activeFilters,
  } = useFiltersContextHelper();
  const { globalFilters, setProductArea } = useIdeasUrlState({ subPageKey });
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!selectedParentFilter) {
      setOpened(false);
    }
  }, [selectedParentFilter]);

  // Derive current selection state for product-area
  const productAreaState = useMemo(() => {
    const appliedFilter = activeFilters?.find(
      (f: any) => (f?.parent || f?.name)?.toLowerCase() === 'product-area'
    );
    const selected: string[] =
      Array.isArray(globalFilters?.productArea) && globalFilters.productArea.length > 0
        ? globalFilters.productArea
        : Array.isArray(appliedFilter?.selected)
          ? appliedFilter.selected
          : [];
    const count = selected.length;

    const options: any[] = (ideasFiltersConfig as any)?.idea?.["product-area"]?.options || [];

    let first: { name?: string; label?: string } | null = null;
    if (count === 1) {
      const firstVal = String(selected[0]).toLowerCase();
      first =
        options.find(
          (o: any) =>
            (o.value?.toLowerCase?.() || o.label?.toLowerCase?.()) === firstVal
        ) || null;
    }

    // Build display names for tooltip when multiple selected
    const buildOptionsMap = (opts: any[]): Record<string, string> => {
      const map: Record<string, string> = {};
      const traverse = (list: any[]) => {
        list.forEach((o: any) => {
          if (!o) return;
          const key = String(o.value || o.label || "").toLowerCase();
          if (key) map[key] = o.label || o.name || key;
          if (Array.isArray(o.childComponents) && o.childComponents.length > 0) {
            traverse(o.childComponents);
          }
        });
      };
      traverse(opts);
      return map;
    };

    let multipleTooltip = "";
    if (count > 1 && Array.isArray(options)) {
      const map = buildOptionsMap(options);
      const names = selected.map((s) => map[String(s).toLowerCase()] || String(s));
      multipleTooltip = names.join(", ");
    }

    return { count, first, multipleTooltip };
  }, [globalFilters?.productArea, ideasFiltersConfig, activeFilters]);

  const trigger = (
    <UnstyledButton
      aria-label="Product area filter"
      style={{
        height: 32,
        boxSizing: "border-box",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "0 8px",
        background: "#FFFFFF",
        width: 145,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
      }}
    >
      {productAreaState.count === 0 && (
        <Group spacing={6} noWrap h={32}>
          <ProductAreaHeaderIcon fill="#6B7280" color="#6B7280" />
          <Text fz={12} c="dimmed" lh={1.2}>
           Select area...
          </Text>
        </Group>
      )}
      {productAreaState.count === 1 && (
        <Group spacing={6} noWrap h={32}>
          <ProductAreaHeaderIcon fill="#6B7280" color="#6B7280" />
          <Text fz={12} lh={1.2}>
            {productAreaState.first?.name || productAreaState.first?.label || "Area"}
          </Text>
        </Group>
      )}
      {productAreaState.count > 1 && (
        <Group spacing={8} noWrap h={32}>
          <Group spacing={6} noWrap>
            <ProductAreaHeaderIcon fill="#6B7280" color="#6B7280" />
            <Text fz={12} lh={1.2}>
              {productAreaState.count}
            </Text>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#5C5CEB",
              }}
            />
          </Group>
          <Tooltip
            maw={600}
            multiline
            label={productAreaState.multipleTooltip}>
            <Text fz={12} lh={1.2}>
              Multiple areas
            </Text>
          </Tooltip>
        </Group>
      )}
      <IconChevronDown size={14} color="#6B7280" />
    </UnstyledButton>
  );

  const resetFilters = (MenuType: "idea" | "evidence", selectedParentFilter?: string | null) => {
    const parentKey = selectedParentFilter ?? "product-area";

    setCheckedComponents((current: any) => {
      const next: { [key: string]: any[] } = {};
      Object.keys(current).forEach((key) => {
        next[key] = key.split(".")[0] === parentKey ? [] : current[key];
      });
      return next;
    });

    if (parentKey === "product-area") {
      setProductArea([]);
    }
  };

  return (
    <Menu
      shadow="md"
      radius="md"
      width={300}
      position="bottom-start"
      opened={opened}
      onChange={(isOpen) => {
        setOpened(isOpen);
        if (isOpen) {
          setSelectedParentFilter("product-area");
        } else {
          setSelectedParentFilter(null);
        }
      }}
    >
      <Menu.Target>
        {trigger}
      </Menu.Target>
      <Menu.Dropdown sx={{ padding: 0, paddingTop: 5 }}>
        <Box>
          <CheckboxFilter
            initialfilters={ideasFiltersConfig.idea}
            MenuType="idea"
            resetFilters={resetFilters}
            ideasFiltersConfig={ideasFiltersConfig}
            setOpened={setOpened}
            setValue={setProductArea}
            hideAddButton
          />
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default StickyProductAreaFilter;
